import { useState } from "react";
import { Button, Tooltip, useTheme } from "@mui/material";
import cn from "../../utils/cn";
import { AI_STATUS, getAIErrorMessage, isFinalAIStatus } from "../../utils/aiInterviewApi";
import { PlanQuestionDetails, questionMeta } from "./AIPlanView";
import { SECTION_LABELS, byPriority, formatTime, humanize, toText } from "./aiShared";

// Live dashboard for "Panelist + AI assistant" interviews, used next to the meeting window.
// Renders a /live payload and the plan's question bank. Never shows AI scores: the API doesn't send them here.

const PURPOSE_LABELS = {
  probe_deeper: "Probe deeper",
  clarify: "Clarify",
  verify_claim: "Verify claim",
  easier: "Easier",
  harder: "Harder",
  missing_point: "Missing point",
};

const MARK_LABELS = { next: "Asking now", asked: "Asked", skipped: "Skipped" };

const WAITING_COPY = {
  SCHEDULED: "The AI assistant joins the meeting about 90 seconds before the start.",
  PREPARING: "The AI assistant joins the meeting about 90 seconds before the start.",
  READY: "The AI assistant joins the meeting about 90 seconds before the start.",
  BOT_JOINING: "The AI assistant is joining the meeting…",
  WAITING_FOR_CANDIDATE: "Waiting for the candidate to join…",
  IN_PROGRESS: "Waiting for the first question…",
  PAUSED: "Paused.",
};

const Panel = ({ title, children, className, action }) => {
  const theme = useTheme();
  return (
    <section
      className={cn("rounded-lg border p-4 flex flex-col gap-3 min-w-0", className)}
      style={{ borderColor: theme.palette.secondary.v4, backgroundColor: theme.palette.primary.main }}
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-600">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
};

const SmallButton = ({ children, onClick, disabled, primary, label }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    className={cn(
      "rounded border px-2 py-0.5 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed",
      primary ? "border-blue-700 bg-blue-700 text-white hover:bg-blue-800" : "border-gray-300 bg-white hover:bg-gray-100"
    )}
  >
    {children}
  </button>
);

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };
  return (
    <SmallButton onClick={copy} label={`Copy: ${text}`}>
      {copied ? "Copied" : "Copy"}
    </SmallButton>
  );
};

const Tick = ({ on }) => (
  <span aria-hidden="true" className={cn("inline-block w-4 shrink-0 text-center font-bold", on ? "text-green-600" : "text-gray-300")}>
    {on ? "✓" : "○"}
  </span>
);

// Whether a bank question is done: asked (seen by the AI or marked) or skipped
export const bankQuestionState = (live, questionId) => {
  const mark = live?.bank_marks?.[questionId]?.status;
  if (mark === "asked" || mark === "skipped") return mark;
  if ((live?.asked_bank_ids || []).includes(questionId)) return "asked";
  return mark === "next" ? "next" : "";
};

const BankQuestion = ({ question, state, suggested, onMark, busy }) => {
  const done = state === "asked" || state === "skipped";
  return (
    <li
      className={cn(
        "rounded-md border p-2 flex flex-col gap-1.5",
        suggested ? "border-blue-500 ring-2 ring-blue-200" : "border-gray-200",
        state === "next" && "border-violet-400 bg-violet-50",
        done && "opacity-50"
      )}
      aria-label={`${toText(question.text)}${state ? ` (${MARK_LABELS[state]})` : ""}${suggested ? " (Suggested next)" : ""}`}
    >
      <div className="flex flex-wrap gap-1">
        {suggested && <span className="rounded-full bg-blue-700 px-2 py-0.5 text-[11px] font-medium text-white">Suggested next</span>}
        {state && (
          <span className="rounded-full border border-gray-300 bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-700">
            {MARK_LABELS[state]}
          </span>
        )}
      </div>
      <details>
        <summary className="cursor-pointer list-none text-sm">
          {toText(question.text)}
          <span className="block text-xs text-gray-500">{questionMeta(question)}</span>
        </summary>
        <div className="mt-2 pl-3 border-l-2 border-gray-200">
          <PlanQuestionDetails question={question} />
        </div>
      </details>
      {!done && (
        <div className="flex flex-wrap gap-1">
          <SmallButton primary onClick={() => onMark(question.question_id, "next")} disabled={busy || state === "next"}>
            Ask next
          </SmallButton>
          <SmallButton onClick={() => onMark(question.question_id, "skipped")} disabled={busy}>Skip</SmallButton>
          <SmallButton onClick={() => onMark(question.question_id, "asked")} disabled={busy}>Mark asked</SmallButton>
        </div>
      )}
    </li>
  );
};

const QuestionBank = ({ live, plan, planError, onReloadPlan, onMark, busyId }) => {
  const suggestedId = live?.suggestions?.next_question?.question_id || null;
  const sections = Array.isArray(plan?.sections) ? plan.sections : [];
  return (
    <Panel title="Question bank" className="lg:max-h-[calc(100dvh-140px)] lg:overflow-y-auto">
      {planError ? (
        <div className="text-sm text-red-700 flex flex-col gap-2 items-start" role="alert">
          {planError}
          {onReloadPlan && <SmallButton onClick={onReloadPlan}>Try again</SmallButton>}
        </div>
      ) : !plan ? (
        <div className="text-sm flex flex-col gap-2 items-start">
          The question bank isn't ready yet.
          {onReloadPlan && <SmallButton onClick={onReloadPlan}>Check again</SmallButton>}
        </div>
      ) : (
        sections.map((section) => (
          <div key={section.section_id} className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold">{toText(section.name) || SECTION_LABELS[section.section_id] || humanize(section.section_id)}</h3>
            <ul className="flex flex-col gap-2">
              {(section.questions || []).map((question) => (
                <BankQuestion
                  key={question.question_id}
                  question={question}
                  state={bankQuestionState(live, question.question_id)}
                  suggested={question.question_id === suggestedId}
                  onMark={onMark}
                  busy={busyId === question.question_id}
                />
              ))}
            </ul>
          </div>
        ))
      )}
    </Panel>
  );
};

const exchangeIndicator = (live) => {
  const current = live?.current;
  if (!current) return "";
  if (current.status === "answering") return "Listening…";
  if (current.status === "asking") return "Asking…";
  return live.suggestions?.for_question_id === current.question_id ? "" : "Analysing…";
};

const CurrentExchange = ({ live, onMark, busyId }) => {
  const current = live?.current;
  const suggestions = live?.suggestions;
  const indicator = exchangeIndicator(live);
  const stale = suggestions && current && suggestions.for_question_id !== current.question_id;
  const nextQuestion = suggestions?.next_question;

  return (
    <Panel title="Current exchange">
      {!current ? (
        <p className="text-base text-gray-600">
          {isFinalAIStatus(live?.status) ? "The interview has ended." : WAITING_COPY[live?.status] || "Waiting…"}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <div>
            <p className="text-xs text-gray-500">
              {current.question_id}
              {current.asked_by ? ` · Asked by ${current.asked_by}` : ""}
            </p>
            <p className="text-lg font-semibold leading-snug">{toText(current.question)}</p>
          </div>
          <div className="rounded-md bg-gray-50 px-3 py-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-medium text-gray-500">Candidate's answer</p>
              {indicator && (
                <span className="text-xs font-medium text-violet-700" role="status">{indicator}</span>
              )}
            </div>
            <p className="text-base whitespace-pre-line">{toText(current.answer) || "…"}</p>
          </div>
        </div>
      )}

      {suggestions && (
        <div className="flex flex-col gap-3 border-t border-gray-200 pt-3">
          {stale && (
            <p className="text-xs text-gray-500">For the previous answer: {toText(suggestions.for_question)}</p>
          )}
          {suggestions.answer_summary && (
            <div>
              <p className="text-xs font-medium text-gray-500">Answer summary</p>
              <p className="text-sm">{toText(suggestions.answer_summary)}</p>
            </div>
          )}
          {(suggestions.covered_points?.length > 0 || suggestions.missing_points?.length > 0) && (
            <ul className="flex flex-col gap-1 text-sm">
              {(suggestions.covered_points || []).map((point, index) => (
                <li key={`c-${index}`} className="flex gap-2"><Tick on /> <span>{toText(point)}</span></li>
              ))}
              {(suggestions.missing_points || []).map((point, index) => (
                <li key={`m-${index}`} className="flex gap-2 text-gray-500">
                  <Tick on={false} /> <span>{toText(point)}<span className="sr-only"> (missing)</span></span>
                </li>
              ))}
            </ul>
          )}
          {suggestions.follow_ups?.length > 0 && (
            <div className="flex flex-col gap-2">
              <p className="text-xs font-medium text-gray-500">Follow-ups</p>
              <div className="grid grid-cols-1 gap-2">
                {suggestions.follow_ups.slice(0, 3).map((followUp, index) => (
                  <Tooltip key={index} title={toText(followUp.why)} arrow placement="top">
                    <div className="rounded-md border border-gray-200 p-2 flex items-start justify-between gap-2">
                      <div className="flex flex-col gap-1">
                        <span className="self-start rounded-full bg-blue-50 border border-blue-200 px-2 py-0.5 text-[11px] font-medium text-blue-800">
                          {PURPOSE_LABELS[followUp.purpose] || humanize(followUp.purpose)}
                        </span>
                        <p className="text-sm">{toText(followUp.text)}</p>
                      </div>
                      <CopyButton text={toText(followUp.text)} />
                    </div>
                  </Tooltip>
                ))}
              </div>
            </div>
          )}
          {nextQuestion && (
            <div className="rounded-md border border-blue-300 bg-blue-50 p-3 flex flex-col gap-2">
              <p className="text-xs font-medium text-blue-800">Suggested next question</p>
              <p className="text-sm">{toText(nextQuestion.text)}</p>
              {nextQuestion.why && <p className="text-xs text-gray-600">{toText(nextQuestion.why)}</p>}
              <div className="flex gap-1">
                {nextQuestion.question_id && (
                  <SmallButton
                    primary
                    onClick={() => onMark(nextQuestion.question_id, "next")}
                    disabled={busyId === nextQuestion.question_id}
                    label="Ask the suggested next question"
                  >
                    Ask next
                  </SmallButton>
                )}
                <CopyButton text={toText(nextQuestion.text)} />
              </div>
            </div>
          )}
        </div>
      )}
    </Panel>
  );
};

const Coverage = ({ coverage }) => {
  const skills = [...(coverage?.skills || [])].sort(byPriority((item) => item.priority));
  const claims = coverage?.claims || [];
  return (
    <Panel title="Coverage">
      {!skills.length && !claims.length && <p className="text-sm text-gray-500">Nothing to track yet.</p>}
      {skills.length > 0 && (
        <ul className="flex flex-col gap-1 text-sm" aria-label="Skills">
          {skills.map((item) => (
            <li key={item.skill} className="flex gap-2 items-baseline">
              <Tick on={item.covered} />
              <span className={cn(!item.covered && "text-gray-600")}>
                {toText(item.skill)}
                {item.priority === "must_have" && <span className="ml-1 text-[11px] font-medium text-red-700">must-have</span>}
                <span className="sr-only">{item.covered ? " (covered)" : " (not covered)"}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
      {claims.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1">Resume claims</p>
          <ul className="flex flex-col gap-1 text-sm" aria-label="Resume claims">
            {claims.map((claim) => (
              <li key={claim.claim_id} className="flex gap-2 items-baseline">
                <Tick on={claim.covered} />
                <span className={cn(!claim.covered && "text-gray-600")}>{toText(claim.text)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Panel>
  );
};

const Notes = ({ notes, onAddNote }) => {
  const theme = useTheme();
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const sorted = [...(notes || [])].sort((a, b) => new Date(b.at) - new Date(a.at));

  const add = async () => {
    if (!text.trim()) return;
    setSaving(true);
    setError("");
    try {
      await onAddNote(text);
      setText("");
    } catch (err) {
      setError(getAIErrorMessage(err, "Couldn't add the note."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel title="Notes">
      <label htmlFor="live-general-notes" className="sr-only">Add a note</label>
      <textarea
        id="live-general-notes"
        value={text}
        onChange={(event) => setText(event.target.value)}
        rows={2}
        placeholder="Add a note for the hiring team"
        className="w-full px-3 py-2 border rounded-md text-sm"
        style={{ borderColor: theme.palette.secondary.v4, color: "#111111", backgroundColor: "#ffffff" }}
      />
      {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
      <div className="flex justify-end">
        <Button
          onClick={add}
          disabled={saving || !text.trim()}
          sx={{ backgroundColor: theme.palette.info.main, color: theme.palette.text.light, textTransform: "none" }}
        >
          {saving ? "Adding..." : "Add note"}
        </Button>
      </div>
      {sorted.length > 0 && (
        <ul className="flex flex-col gap-2 text-sm">
          {sorted.map((note, index) => (
            <li key={`${note.at}-${index}`} className="border-t border-gray-100 pt-2">
              <p className="whitespace-pre-line">{toText(note.text)}</p>
              <p className="text-xs text-gray-500">
                {[toText(note.by), formatTime(note.at), note.question_id].filter(Boolean).join(" · ")}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
};

export const DisclosureBanner = ({ onDone }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const done = async () => {
    setSaving(true);
    setError("");
    try {
      await onDone();
    } catch (err) {
      setError(getAIErrorMessage(err, "Couldn't save that. Try again."));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="rounded-lg border border-amber-400 bg-amber-50 px-4 py-3 flex flex-wrap items-center justify-between gap-3" role="alert">
      <p className="text-base font-medium text-amber-900">Tell the candidate that an AI assistant is transcribing.</p>
      <div className="flex items-center gap-2">
        {error && <span className="text-sm text-red-700">{error}</span>}
        <button
          type="button"
          onClick={done}
          disabled={saving}
          className="rounded-md bg-amber-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
        >
          {saving ? "Saving..." : "Done"}
        </button>
      </div>
    </div>
  );
};

export default function AILiveDashboard({ live, plan, planError, onReloadPlan, onDisclosed, onMarkBank, onAddNote }) {
  const [busyId, setBusyId] = useState("");
  const [markError, setMarkError] = useState("");

  const mark = async (questionId, status) => {
    setBusyId(questionId);
    setMarkError("");
    try {
      await onMarkBank(questionId, status);
    } catch (err) {
      setMarkError(getAIErrorMessage(err, "Couldn't update the question."));
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {!live.disclosure && live.status !== AI_STATUS.CANCELLED && <DisclosureBanner onDone={onDisclosed} />}
      {markError && <p className="text-sm text-red-700" role="alert">{markError}</p>}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_minmax(0,1fr)] items-start">
        <QuestionBank live={live} plan={plan} planError={planError} onReloadPlan={onReloadPlan} onMark={mark} busyId={busyId} />
        <CurrentExchange live={live} onMark={mark} busyId={busyId} />
        <div className="flex flex-col gap-3 min-w-0">
          <Coverage coverage={live.coverage} />
          <Notes notes={live.notes} onAddNote={onAddNote} />
        </div>
      </div>
    </div>
  );
}
